import { useState, useEffect, useRef } from 'react';
import { TaskService, submitTask, getTaskStatus } from '../services';
import { 
  saveSuccessRecords,
  saveToLocalStorage,
  loadFromLocalStorage,
  sleep
} from '../utils';
import { STORAGE_KEYS, RETRY_CONFIG } from '../constants/config';

export const useBatchTaskProcessor = (addLog) => {
  const [batchTasks, setBatchTasks] = useState([]);
  const [isAllTasksCompleted, setIsAllTasksCompleted] = useState(false);
  const [isValidatingBatch, setIsValidatingBatch] = useState(false);
  
  const pollTimersRef = useRef(new Map());

  // 监听批量任务状态
  useEffect(() => {
    if (batchTasks.length > 0) {
      const allCompleted = batchTasks.every(task =>
        task.status === 'completed' ||
        task.status === 'failed' ||
        task.status === 'timeout'
      );
      setIsAllTasksCompleted(allCompleted);

      if (allCompleted) {
        const completed = batchTasks.filter(t => t.status === 'completed').length;
        const failed = batchTasks.filter(t => t.status === 'failed' || t.status === 'timeout').length;
        const total = batchTasks.length;
        
        addLog(`全部批量任务已完成，成功: ${completed}/${total}`, completed === total ? 'success' : 'info');

        // 保存成功记录
        if (completed > 0) {
          const successTasks = batchTasks.filter(t => t.status === 'completed')
            .map(t => ({ 
              email: t.email, 
              code: t.code, 
              account: t.account,
              time: new Date().toISOString(),
              type: 'batch'
            }));
          
          saveSuccessRecords(successTasks);
          addLog(`已保存 ${completed} 个成功记录到本地`, 'success');
        }

        // 保存失败任务
        if (failed > 0) {
          const failedTasks = batchTasks.filter(t => t.status === 'failed' || t.status === 'timeout')
            .map(t => ({ account: t.account, code: t.code }));
          saveToLocalStorage(STORAGE_KEYS.FAILED_TASKS, failedTasks);
          addLog(`已保存 ${failed} 个失败的任务，可点击"加载失败任务"按钮重新执行`, 'info');
        }
      }
    } else {
      setIsAllTasksCompleted(false);
    }
  }, [batchTasks, addLog]);

  // 处理批量任务提交
  const handleBatchSubmit = async (validPairs) => {
    setIsValidatingBatch(false);
    addLog(`开始处理批量任务，共 ${validPairs.length} 个账号`, 'info');

    // 创建初始任务状态
    const initialTasks = validPairs.map((pair, index) => ({
      id: `task-${Date.now()}-${index}`,
      account: pair.account,
      code: pair.code,
      email: pair.email || pair.tokenInfo?.user_info?.email || '',
      status: 'pending',
      createdAt: new Date().toISOString(),
      tokenInfo: pair.tokenInfo,
      provider: pair.provider || '',
      providerDisplay: pair.providerDisplay || '',
      rateLimit: pair.rateLimit || null,
      isOverwrite: pair.isOverwrite || false,
      retryCount: 0
    }));

    // 立即设置所有任务为pending状态，确保进度条显示正确的总数
    setBatchTasks(initialTasks);

    const providerTimestamps = {};

    for (let i = 0; i < initialTasks.length; i++) {
      const task = initialTasks[i];
      const pair = validPairs[i];
      const rateLimit = pair.rateLimit;
      const provider = task.provider;

      if (rateLimit && provider) {
        if (!providerTimestamps[provider]) {
          providerTimestamps[provider] = [];
        }
        const timestamps = providerTimestamps[provider];
        const now = Date.now();
        const periodMs = rateLimit.period_seconds * 1000;

        while (timestamps.length > 0 && now - timestamps[0] >= periodMs) {
          timestamps.shift();
        }

        if (timestamps.length >= rateLimit.max_requests) {
          const waitTime = periodMs - (now - timestamps[0]) + 1000;
          addLog(
            `渠道商限速: 等待 ${Math.ceil(waitTime / 1000)} 秒后继续提交 ` +
            `(${i + 1}/${initialTasks.length})`,
            'info'
          );
          await sleep(waitTime);

          const newNow = Date.now();
          while (timestamps.length > 0 && newNow - timestamps[0] >= periodMs) {
            timestamps.shift();
          }
        }

        timestamps.push(Date.now());
      }

      // 串行提交：等待当前任务提交完成后再提交下一个
      await processBatchTask(task, pair, i + 1, initialTasks.length);

      // 无限速配置时，也保留小间隔避免瞬间并发
      if (i < initialTasks.length - 1 && !rateLimit) {
        await sleep(500);
      }
    }

    addLog(`所有任务已提交，等待处理完成...`, 'info');
  };

  // 处理单个批量任务
  const processBatchTask = async (task, pair, index, total, retryCount = 0) => {
    const { MAX_RETRIES, RETRY_DELAY } = RETRY_CONFIG;

    // 更新任务状态为processing
    setBatchTasks(prevTasks => {
      const newTasks = [...prevTasks];
      const taskIndex = newTasks.findIndex(t => t.id === task.id);
      if (taskIndex !== -1) {
        newTasks[taskIndex].status = 'processing';
      }
      return newTasks;
    });

    try {
      addLog(`处理任务 ${index}/${total}: ${task.email || task.account} (重试: ${retryCount}/${MAX_RETRIES})`, 'info');

      const taskData = {
        access_token: pair.tokenInfo.access_token,
        redeem_code: task.code,
        original_input: task.account,
        email: task.email,
        account_info: pair.tokenInfo.account_info,
        provider: task.provider || '',
        user_id: pair.tokenInfo?.user_info?.user_id || '',
        allow_overwrite: task.isOverwrite || false,
      };

      const result = await submitTask(taskData);

      if (result.success) {
        const taskId = result.task_id;
        const channelInfo = result.channel ? ` (渠道 ${result.channel})` : '';
        addLog(`任务 ${index} 提交成功，任务ID: ${taskId}${channelInfo}`, 'success');

        if (result.immediate) {
          setBatchTasks(prevTasks => {
            const newTasks = [...prevTasks];
            const taskIndex = newTasks.findIndex(t => t.id === task.id);
            if (taskIndex !== -1) {
              newTasks[taskIndex].taskId = taskId;
              newTasks[taskIndex].status = 'completed';
              newTasks[taskIndex].result = result.result || '兑换成功';
              newTasks[taskIndex].retryCount = retryCount;
            }
            return newTasks;
          });
          addLog(`任务 ${task.email || taskId} 完成: ${result.result || '兑换成功'}`, 'success');
        } else {
          setBatchTasks(prevTasks => {
            const newTasks = [...prevTasks];
            const taskIndex = newTasks.findIndex(t => t.id === task.id);
            if (taskIndex !== -1) {
              newTasks[taskIndex].taskId = taskId;
              newTasks[taskIndex].channel = result.channel;
              newTasks[taskIndex].retryCount = retryCount;
            }
            return newTasks;
          });

          pollBatchTaskStatus(task.id, taskId, retryCount);
        }
      } else {
        throw new Error(result.message || '任务提交失败');
      }
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        addLog(`任务 ${index} 提交失败: ${error.message}，准备重试...`, 'error');
        await sleep(RETRY_DELAY);
        return await processBatchTask(task, pair, index, total, retryCount + 1);
      } else {
        addLog(`任务 ${index} 提交失败（已达最大重试次数）: ${error.message}`, 'error');

        setBatchTasks(prevTasks => {
          const newTasks = [...prevTasks];
          const taskIndex = newTasks.findIndex(t => t.id === task.id);
          if (taskIndex !== -1) {
            newTasks[taskIndex].status = 'failed';
            newTasks[taskIndex].error = error.message;
            newTasks[taskIndex].retryCount = retryCount;
          }
          return newTasks;
        });
      }
    }
  };

  // 轮询批量任务状态
  const pollBatchTaskStatus = async (localTaskId, apiTaskId, retryCount = 0) => {
    const { POLL_INTERVAL, MAX_POLL_RETRIES } = RETRY_CONFIG;
    let retries = 0;

    const checkStatus = async () => {
      try {
        retries++;

        if (retries > MAX_POLL_RETRIES) {
          const result = await getTaskStatus(apiTaskId).catch(() => ({}));
          if (result.expires_at && (Date.now() / 1000) < result.expires_at) {
            retries = Math.floor(MAX_POLL_RETRIES * 0.5);
          } else {
            setBatchTasks(prevTasks => {
              const newTasks = [...prevTasks];
              const taskIndex = newTasks.findIndex(t => t.id === localTaskId);
              if (taskIndex !== -1) {
                newTasks[taskIndex].status = 'timeout';
                newTasks[taskIndex].error = '任务处理超时';
                newTasks[taskIndex].retryCount = retryCount;
              }
              return newTasks;
            });
            return;
          }
        }

        const result = await getTaskStatus(apiTaskId);

        if (!result.success) {
          throw new Error(result.message || '获取任务状态失败');
        }

        const status = result.status;

        if (status === 'completed' || status === 'failed') {
          setBatchTasks(prevTasks => {
            const newTasks = [...prevTasks];
            const taskIndex = newTasks.findIndex(t => t.id === localTaskId);
            if (taskIndex !== -1) {
              newTasks[taskIndex].status = status;
              newTasks[taskIndex].result = result.result;
              newTasks[taskIndex].retryCount = retryCount;
              if (status === 'failed') {
                newTasks[taskIndex].error = result.result || '任务处理失败';
              }
            }
            return newTasks;
          });

          const taskInfo = batchTasks.find(t => t.id === localTaskId);
          addLog(
            `任务 ${taskInfo?.email || apiTaskId} ${status === 'completed' ? '完成' : '失败'}: ${result.result || ''}`,
            status === 'completed' ? 'success' : 'error'
          );
        } else if ((status === 'processing' || status === 'pending') && retries < MAX_POLL_RETRIES) {
          setBatchTasks(prevTasks => {
            const newTasks = [...prevTasks];
            const taskIndex = newTasks.findIndex(t => t.id === localTaskId);
            if (taskIndex !== -1) {
              newTasks[taskIndex].status = status;
            }
            return newTasks;
          });
          
          const interval = (result.expires_at && (Date.now() / 1000) < result.expires_at) ? 15000 : POLL_INTERVAL;
          const timerId = setTimeout(checkStatus, interval);
          pollTimersRef.current.set(localTaskId, timerId);
        }
      } catch (error) {
        setBatchTasks(prevTasks => {
          const newTasks = [...prevTasks];
          const taskIndex = newTasks.findIndex(t => t.id === localTaskId);
          if (taskIndex !== -1) {
            newTasks[taskIndex].status = 'failed';
            newTasks[taskIndex].error = error.message;
            newTasks[taskIndex].retryCount = retryCount;
          }
          return newTasks;
        });

        const taskInfo = batchTasks.find(t => t.id === localTaskId);
        addLog(`任务 ${taskInfo?.email || apiTaskId} 状态检查失败: ${error.message}`, 'error');
      }
    };

    checkStatus();
  };

  // 重置批量任务
  const resetBatchTasks = () => {
    // 清理所有定时器
    pollTimersRef.current.forEach(timerId => clearTimeout(timerId));
    pollTimersRef.current.clear();
    
    setBatchTasks([]);
    setIsAllTasksCompleted(false);
    setIsValidatingBatch(false);
    addLog('已重置批量任务，可以开始新的批量充值', 'info');
  };

  // 清理函数
  useEffect(() => {
    return () => {
      pollTimersRef.current.forEach(timerId => clearTimeout(timerId));
      pollTimersRef.current.clear();
    };
  }, []);

  return {
    batchTasks,
    isAllTasksCompleted,
    isValidatingBatch,
    handleBatchSubmit,
    resetBatchTasks,
    setBatchTasks,
    setIsValidatingBatch
  };
};